import type { ItemCategory } from "@/lib/types";
import { validateSharedFreeTextContactField } from "@/lib/listing/listingFreeTextPolicy";
import {
  composeItemRequestTitle,
  normalizeItemRequestNotes,
  ITEM_REQUEST_NOTES_MAX_LEN,
} from "./composeRequestTitle";
import {
  normalizePreferenceAttrs,
  type PreferenceAttrs,
} from "./preferenceAttrs";

export const ITEM_REQUEST_MAX_PRICE_MIN = 20_000;
export const ITEM_REQUEST_MAX_PRICE_MAX = 10_000_000;
/** Soft rate limit for auto-approve path (per user / 24h). */
export const ITEM_REQUEST_DAILY_CREATE_LIMIT = 10;

export type ItemRequestCaptureInput = {
  category: string;
  subcategory: string;
  categoryName: string;
  subcategoryName: string;
  userLocation: string;
  urgency: string;
  maxPrice?: number | null;
  /** Optional notes — never the sole identity of the request */
  notes?: string | null;
  /** Optional listing-style prefs (D12) — never required */
  preferenceAttrs?: PreferenceAttrs | null;
};

export type ItemRequestCaptureValid = {
  ok: true;
  category: ItemCategory;
  subcategory: string;
  title: string;
  description: string;
  userLocation: string;
  urgency: "low" | "medium" | "high";
  maxPrice: number | undefined;
  preferenceAttrs: PreferenceAttrs;
};

export type ItemRequestCaptureInvalid = {
  ok: false;
  error: string;
  code:
    | "missing_category"
    | "missing_subcategory"
    | "missing_location"
    | "invalid_urgency"
    | "invalid_max_price"
    | "notes_too_long"
    | "spam_notes"
    | "rate_limited";
};

export type ItemRequestCaptureResult =
  | ItemRequestCaptureValid
  | ItemRequestCaptureInvalid;

const URGENCIES = new Set(["low", "medium", "high"]);

/**
 * Structured capture validation — category/subcategory required; title composed.
 * Free-text notes alone cannot form a valid request.
 */
export function validateItemRequestCapture(
  input: ItemRequestCaptureInput,
  options?: { recentCreateCount24h?: number },
): ItemRequestCaptureResult {
  const category = (input.category || "").trim();
  const subcategory = (input.subcategory || "").trim();
  const categoryName = (input.categoryName || "").trim();
  const subcategoryName = (input.subcategoryName || "").trim();
  const userLocation = (input.userLocation || "").trim();
  const urgency = (input.urgency || "").trim().toLowerCase();

  if (!category || !categoryName) {
    return {
      ok: false,
      error: "Please select a category.",
      code: "missing_category",
    };
  }

  if (!subcategory || !subcategoryName) {
    return {
      ok: false,
      error: "Please select a subcategory so we know exactly what you want.",
      code: "missing_subcategory",
    };
  }

  if (!userLocation) {
    return {
      ok: false,
      error: "Please set your location in your profile first.",
      code: "missing_location",
    };
  }

  if (!URGENCIES.has(urgency)) {
    return {
      ok: false,
      error: "Please choose how soon you need it.",
      code: "invalid_urgency",
    };
  }

  let maxPrice: number | undefined;
  if (input.maxPrice != null && input.maxPrice !== undefined) {
    const n = Number(input.maxPrice);
    if (
      !Number.isFinite(n) ||
      n < ITEM_REQUEST_MAX_PRICE_MIN ||
      n > ITEM_REQUEST_MAX_PRICE_MAX
    ) {
      return {
        ok: false,
        error: `Maximum price must be between ₦${ITEM_REQUEST_MAX_PRICE_MIN.toLocaleString("en-NG")} and ₦${ITEM_REQUEST_MAX_PRICE_MAX.toLocaleString("en-NG")}.`,
        code: "invalid_max_price",
      };
    }
    maxPrice = Math.round(n);
  }

  const rawNotes = input.notes ?? "";
  if (rawNotes.trim().length > ITEM_REQUEST_NOTES_MAX_LEN) {
    return {
      ok: false,
      error: `Notes must be at most ${ITEM_REQUEST_NOTES_MAX_LEN} characters.`,
      code: "notes_too_long",
    };
  }

  // H13 — same contact/link gate as listing free-text (buyer-facing copy).
  const notesPolicyError = validateSharedFreeTextContactField(rawNotes, {
    audience: "request",
  });
  if (notesPolicyError) {
    return {
      ok: false,
      error: notesPolicyError,
      code: "spam_notes",
    };
  }

  const recent = options?.recentCreateCount24h;
  if (typeof recent === "number" && recent >= ITEM_REQUEST_DAILY_CREATE_LIMIT) {
    return {
      ok: false,
      error: "You’ve submitted several requests today. Try again tomorrow.",
      code: "rate_limited",
    };
  }

  const title = composeItemRequestTitle({
    categoryName,
    subcategoryName,
  });
  const description = normalizeItemRequestNotes(rawNotes);
  const preferenceAttrs = normalizePreferenceAttrs(input.preferenceAttrs ?? {});

  return {
    ok: true,
    category: category as ItemCategory,
    subcategory,
    title,
    description,
    userLocation,
    urgency: urgency as "low" | "medium" | "high",
    maxPrice,
    preferenceAttrs,
  };
}
